import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Edit, AlertCircle, Package2, Image } from "lucide-react";
import { useService } from "@/hooks/useServices";
import { PhotoGallery } from "@/components/ui/PhotoGallery";
import { PhotoParentType } from "@/types/photos";
import { usePhotos } from "@/hooks/usePhotos";
import ServiceOptionsList from "./ServiceOptionsList";
import EditServiceModal from "../modals/EditServiceModal";

interface ServiceDetailDrawerProps {
  serviceId: string;
  open: boolean;
  onClose: () => void;
}

export default function ServiceDetailDrawer({ serviceId, open, onClose }: ServiceDetailDrawerProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const { data: service, loading, error } = useService(serviceId);
  const { photos } = usePhotos(PhotoParentType.Service, serviceId);

  if (loading) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (error || !service) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Service Details</SheetTitle>
            <SheetDescription>Service information</SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load service details: {error || "Service not found"}
              </AlertDescription>
            </Alert>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <div className="flex items-start justify-between">
              <div>
                <SheetTitle className="text-xl">{service.name}</SheetTitle>
                <SheetDescription>
                  Service ID: {service.id}
                </SheetDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={service.isActive ? "default" : "secondary"}>
                  {service.isActive ? "Active" : "Inactive"}
                </Badge>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Service Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Service Information</h3>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-muted-foreground">Vendor ID</label>
                  <p>{service.vendorId}</p>
                </div>
                <div>
                  <label className="font-medium text-muted-foreground">Service Type ID</label>
                  <p>{service.serviceTypeId}</p>
                </div>
                <div>
                  <label className="font-medium text-muted-foreground">Category ID</label>
                  <p>{service.serviceCategoryId}</p>
                </div>
                <div>
                  <label className="font-medium text-muted-foreground">Subcategory ID</label>
                  <p>{service.serviceSubCategoryId}</p>
                </div>
              </div>

              {service.description && (
                <div>
                  <label className="font-medium text-muted-foreground">Description</label>
                  <p className="mt-1">{service.description}</p>
                </div>
              )}

              {service.notes && (
                <div>
                  <label className="font-medium text-muted-foreground">Notes</label>
                  <p className="mt-1">{service.notes}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-muted-foreground">Created</label>
                  <p>{new Date(service.createdAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="font-medium text-muted-foreground">Updated</label>
                  <p>{new Date(service.updatedAt).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Service Photos */}
            {photos.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Photos</h3>
                </div>

                <PhotoGallery
                  photos={photos}
                  parentType={PhotoParentType.Service}
                  parentId={service.id}
                  editable={false}
                />
              </div>
            )}

            {/* Service Options */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Package2 className="h-5 w-5" />
                <h3 className="text-lg font-semibold">Service Options</h3>
              </div>

              <ServiceOptionsList serviceId={service.id} />
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <EditServiceModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        service={service}
      />
    </>
  );
}